import { useInput, useInputIcon, wrappedInputPropsAdapter } from "../../input";
import { Box } from "../../box";
import { CaretIcon } from "../../icons";
import { forwardRef, useCallback, useMemo } from "react";
import { Div, HtmlButton } from "../../html";
import { cssModule, isNil, isNilOrEmpty, mergeClasses, mergeProps, omitProps, useChainedEventCallback, useControllableState, useEventCallback, useFocusWithin, useRefState } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { useFieldInputProps } from "../../field";
import { useInputGroupProps } from "../../input-group";
import { useToolbarProps } from "../../toolbar";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DefaultElement = "input";

function Spinner({
  disableDecrement,
  disableIncrement,
  onDecrement,
  onFocus,
  onIncrement,
  ...rest
}) {
  const handleIncrement = useEventCallback(event => {
    onIncrement(event);
  });
  const handleDecrement = useEventCallback(event => {
    onDecrement(event);
  });
  return /*#__PURE__*/_jsxs(Div, { ...mergeProps(rest, {
      "aria-hidden": true,
      className: "o-ui-number-input-spinner"
    }),
    children: [/*#__PURE__*/_jsx(HtmlButton, {
      "aria-label": "Increment value",
      className: "o-ui-number-input-spinner-increment",
      disabled: disableIncrement,
      onClick: handleIncrement,
      onFocus: onFocus,
      tabIndex: -1,
      type: "button",
      children: /*#__PURE__*/_jsx(CaretIcon, {
        size: "xs"
      })
    }), /*#__PURE__*/_jsx(HtmlButton, {
      "aria-label": "Decrement value",
      className: "o-ui-number-input-spinner-decrement",
      disabled: disableDecrement,
      onClick: handleDecrement,
      onFocus: onFocus,
      tabIndex: -1,
      type: "button",
      children: /*#__PURE__*/_jsx(CaretIcon, {
        size: "xs",
        transform: "rotate(180deg)"
      })
    })]
  });
}

function countDecimalPlaces(value) {
  return (value.toString().split(".")[1] || []).length;
}

function toNumber(value) {
  if (isNilOrEmpty(value)) {
    return null;
  }

  const result = parseFloat(value);

  if (isNaN(result)) {
    return null;
  }

  return result;
}

function toFixed(value, precision) {
  return parseFloat(value.toFixed(precision));
}

export function InnerNumberInput(props) {
  const [toolbarProps] = useToolbarProps();
  const [fieldProps] = useFieldInputProps();
  const [inputGroupProps, isInGroup] = useInputGroupProps();
  const contextualProps = mergeProps({}, omitProps(toolbarProps, ["orientation"]), fieldProps, inputGroupProps);
  const {
    active,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    as = DefaultElement,
    autoFocus,
    defaultValue,
    disabled,
    fluid,
    focus,
    forwardedRef,
    hover,
    icon,
    id,
    loading,
    max,
    min,
    onBlur,
    onChange,
    onFocus,
    onValueChange,
    placeholder,
    readOnly,
    required,
    step = 1,
    validationState,
    value: valueProp,
    wrapperProps: {
      as: wrapperAs = "div",
      ...userWrapperProps
    } = {},
    ...rest
  } = mergeProps(props, wrappedInputPropsAdapter(contextualProps));

  if (isNil(ariaLabel) && isNil(ariaLabelledBy) && isNil(placeholder)) {
    console.error("An input component must either have an \"aria-label\" attribute, an \"aria-labelledby\" attribute or a \"placeholder\" attribute.");
  }

  const fluidValue = useResponsiveValue(fluid);
  const [inputValueRef, setInputValue] = useRefState("");
  const [value, setValue] = useControllableState(valueProp, defaultValue, null, {
    onChange: useCallback((newValue, {
      isControlled,
      isInitial
    }) => {
      // Keep input value "mostly" in sync with the initial or controlled value.
      if (isInitial || isControlled) {
        const rawValue = isNil(newValue) ? "" : newValue.toString();
        setInputValue(rawValue);
      }

      return undefined;
    }, [setInputValue])
  });

  const updateValue = (event, newValue) => {
    if (newValue !== value) {
      setValue(newValue);

      if (!isNil(onValueChange)) {
        onValueChange(event, newValue);
      }
    }

    const newInputValue = isNil(newValue) ? "" : newValue.toString();

    if (newInputValue !== inputValueRef.current) {
      setInputValue(newInputValue, true);
    }
  };

  const validateRange = useCallback(newValue => {
    let isAboveMax = false;
    let isBelowMin = false;

    if (!isNil(newValue)) {
      if (!isNil(max) && newValue > max) {
        isAboveMax = true;
      } else if (!isNil(min) && newValue < min) {
        isBelowMin = true;
      }
    }

    return {
      isAboveMax,
      isBelowMin,
      isInRange: !isAboveMax && !isBelowMin
    };
  }, [min, max]);

  const clampOrSetValue = (event, newValue) => {
    if (!isNil(newValue)) {
      const {
        isAboveMax,
        isBelowMin,
        isInRange
      } = validateRange(newValue);

      if (isInRange) {
        updateValue(event, newValue);
      } else {
        if (isBelowMin) {
          updateValue(event, min);
        } else if (isAboveMax) {
          updateValue(event, max);
        }
      }
    }
  };

  const applyStep = (event, factor) => {
    const inputValue = toNumber(inputValueRef.current);

    if (!isNil(inputValue)) {
      const precision = Math.max(countDecimalPlaces(inputValue), countDecimalPlaces(step));
      const newValue = toFixed(inputValue + factor * step, precision);
      clampOrSetValue(event, newValue);
    } else {
      clampOrSetValue(event, factor * step);
    }
  };

  const handleChange = useChainedEventCallback(onChange, event => {
    setInputValue(event.target.value, true);
  });
  const handleIncrement = useEventCallback(event => {
    applyStep(event, 1);
  });
  const handleDecrement = useEventCallback(event => {
    applyStep(event, -1);
  });
  const handleStepperFocus = useEventCallback(() => {
    inputRef.current.focus();
  });
  const focusWithinProps = useFocusWithin({
    onBlur: useEventCallback(event => {
      clampOrSetValue(event, toNumber(inputValueRef.current));

      if (!isNil(onBlur)) {
        onBlur(event);
      }
    }),
    onFocus: useEventCallback(event => {
      if (!isNil(onFocus)) {
        onFocus(event);
      }
    })
  });
  const {
    inputProps,
    inputRef,
    wrapperProps
  } = useInput({
    active,
    autoFocus,
    cssModule: "o-ui-number-input",
    disabled,
    fluid: fluidValue,
    focus,
    forwardedRef,
    hover,
    id,
    loading,
    onChange: handleChange,
    placeholder,
    readOnly,
    required,
    type: "number",
    validationState,
    value: inputValueRef.current
  }); // eslint-disable-next-line react-hooks/exhaustive-deps

  const numericInputValue = useMemo(() => toNumber(inputValueRef.current), [inputValueRef.current]);
  const iconMarkup = useInputIcon(icon, {
    disabled
  });

  const content = /*#__PURE__*/_jsxs(_Fragment, {
    children: [iconMarkup, /*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        as,
        max,
        min,
        step
      }, inputProps)
    }), /*#__PURE__*/_jsx(Spinner, {
      "aria-hidden": loading,
      disableDecrement: readOnly || disabled || !isNil(numericInputValue) && numericInputValue <= min,
      disableIncrement: readOnly || disabled || !isNil(numericInputValue) && numericInputValue >= max,
      onDecrement: handleDecrement,
      onFocus: handleStepperFocus,
      onIncrement: handleIncrement
    })]
  });

  return /*#__PURE__*/_jsx(Box, { ...mergeProps(userWrapperProps, wrapperProps, {
      as: wrapperAs,
      className: mergeClasses(cssModule("o-ui-input", iconMarkup && "has-icon", isInGroup && "in-group"), cssModule("o-ui-number-input", isInGroup && "in-group"))
    }, focusWithinProps),
    children: content
  });
}
InnerNumberInput.defaultElement = DefaultElement;
export const NumberInput = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerNumberInput, { ...props,
  forwardedRef: ref
}));